import { Link } from "react-router-dom";
import {
  Phone,
  ClipboardList,
  Check,
  Star,
  ShieldCheck,
  Clock,
  Sprout,
  Wind,
  Scissors,
  Ruler,
  Calendar,
  Home as HomeIcon,
  Building2,
  Trash2,
  ArrowRight,
  MapPin,
} from "lucide-react";
import { ASSETS, COMPANY, SERVICES } from "@/lib/site";

const ICONS = {
  Scissors,
  Sprout,
  Ruler,
  Wind,
  Trash2,
  Calendar,
  Home: HomeIcon,
  Building2,
};

const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-[100svh] flex items-center overflow-hidden"
  >
    <video
      data-testid="hero-video"
      className="absolute inset-0 h-full w-full object-cover"
      src={ASSETS.heroVideo}
      autoPlay
      muted
      loop
      playsInline
      poster={ASSETS.photos.bigHome}
    />
    <div className="absolute inset-0 hero-gradient" />
    <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-32">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-xs md:text-sm font-semibold uppercase tracking-widest">
          <MapPin className="h-3.5 w-3.5 text-[#5BB23B]" />
          Serving {COMPANY.serviceAreaShort}
        </div>
        <h1
          data-testid="hero-headline"
          className="mt-6 text-white font-display font-extrabold leading-[1.02] text-4xl sm:text-5xl lg:text-7xl"
        >
          {COMPANY.name}
        </h1>
        <p
          data-testid="hero-tagline"
          className="mt-5 text-white/90 text-lg md:text-2xl max-w-2xl"
        >
          {COMPANY.tagline}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <a
            href={COMPANY.telHref}
            data-testid="hero-call-link"
            className="group inline-flex items-center gap-3 bg-white text-[#0E3A1F] rounded-full px-6 py-4 font-bold text-lg md:text-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
          >
            <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#5BB23B] text-white">
              <Phone className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[10px] tracking-widest uppercase text-[#4B5563]">
                Call Now
              </span>
              <span className="block text-xl md:text-2xl font-extrabold">
                {COMPANY.phoneDisplay}
              </span>
            </span>
          </a>
          <Link
            to="/quote"
            data-testid="hero-quote-button"
            className="sg-btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 font-bold text-base md:text-lg"
          >
            <ClipboardList className="h-5 w-5" />
            Request a Free Quote
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <ul className="mt-10 flex flex-wrap gap-4 text-white/90">
          {["Locally owned", "Free quotes", "Reliable schedule"].map((t) => (
            <li
              key={t}
              className="inline-flex items-center gap-2 text-sm font-semibold"
            >
              <Check className="h-4 w-4 text-[#5BB23B]" /> {t}
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center text-white/80 text-xs uppercase tracking-widest">
      <span>Scroll</span>
      <span className="mt-2 h-8 w-px bg-white/50" />
    </div>
  </section>
);

const TrustBar = () => (
  <section className="bg-[#F5F5F0] border-y border-[#D4D4CE]" data-testid="trust-bar">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
      {[
        { icon: ShieldCheck, label: "Insured & Dependable" },
        { icon: Clock, label: "On-Time, Every Time" },
        { icon: Star, label: "5-Star Local Service" },
        { icon: Sprout, label: "Healthier Lawns" },
      ].map(({ icon: Icon, label }) => (
        <div
          key={label}
          className="flex items-center gap-3 text-[#0E3A1F]"
        >
          <Icon className="h-6 w-6 text-[#5BB23B]" />
          <span className="font-semibold text-sm md:text-base">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

const IntroSection = () => (
  <section className="py-20 md:py-28 reveal" data-testid="intro-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <div className="relative">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <img
            src={ASSETS.photos.bigHome}
            alt="Beautifully maintained front lawn"
            className="w-full h-[460px] md:h-[560px] object-cover"
          />
        </div>
        <div className="absolute -bottom-8 -right-4 md:right-6 bg-white shadow-xl rounded-2xl p-5 max-w-[260px] border border-[#D4D4CE]">
          <div className="text-3xl font-display font-extrabold text-[#0E3A1F]">
            100%
          </div>
          <div className="text-sm text-[#4B5563]">
            Locally owned &amp; operated — your neighbors, your lawn.
          </div>
        </div>
      </div>
      <div>
        <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
          About Southern Greens
        </span>
        <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
          Hardworking lawn care, done right the first time.
        </h2>
        <p className="mt-5 text-[#4B5563] text-lg leading-relaxed">
          We’re a local lawn care crew that treats every yard like our own.
          Whether you need a one-time cleanup or weekly maintenance, we show
          up when we say we will, do the work the right way, and leave your
          property looking sharp.
        </p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-[#0A0A0A]">
          {[
            "Easy scheduling & honest pricing",
            "Residential & commercial",
            "Sharp blades, clean cuts",
            "Cleanup after every visit",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <Check className="h-5 w-5 text-[#5BB23B] mt-0.5" />
              <span className="font-medium">{t}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/quote"
            data-testid="intro-quote-button"
            className="sg-btn-dark rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2"
          >
            Get My Free Quote <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={COMPANY.telHref}
            data-testid="intro-call-link"
            className="inline-flex items-center gap-2 text-[#0E3A1F] font-bold underline-offset-4 hover:underline"
          >
            <Phone className="h-5 w-5" /> {COMPANY.phoneDisplay}
          </a>
        </div>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section
    className="py-20 md:py-28 bg-[#F5F5F0] reveal"
    data-testid="services-preview"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
            What We Do
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
            Full-service lawn care, top to bottom.
          </h2>
        </div>
        <Link
          to="/services"
          data-testid="services-preview-all"
          className="inline-flex items-center gap-2 text-[#0E3A1F] font-semibold hover:gap-3 transition-all"
        >
          View all services <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SERVICES.map((s) => {
          const Icon = ICONS[s.icon] || Sprout;
          return (
            <Link
              key={s.slug}
              to="/services"
              data-testid={`service-card-${s.slug}`}
              className="group bg-white border border-[#D4D4CE] rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300 hover:shadow-xl hover:border-[#5BB23B]/40"
            >
              <div className="h-12 w-12 rounded-xl bg-[#0E3A1F]/5 text-[#0E3A1F] group-hover:bg-[#5BB23B] group-hover:text-white flex items-center justify-center transition-colors">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display font-semibold text-lg text-[#0A0A0A]">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">
                {s.short}
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-[#0E3A1F] text-sm font-semibold opacity-70 group-hover:opacity-100">
                Learn more <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const GalleryStrip = () => (
  <section className="py-20 md:py-28 reveal" data-testid="gallery-strip">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
            Recent Work
          </span>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
            Yards we’re proud of.
          </h2>
        </div>
        <Link
          to="/gallery"
          data-testid="gallery-strip-all"
          className="inline-flex items-center gap-2 text-[#0E3A1F] font-semibold hover:gap-3 transition-all"
        >
          See full gallery <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ASSETS.photos.mowLines,
          ASSETS.photos.bigHome,
          ASSETS.photos.backyard,
          ASSETS.photos.fencedYard,
          ASSETS.photos.sideYardStripes,
          ASSETS.photos.curbsideMow,
        ].map((src, i) => (
          <div
            key={src}
            className={`relative overflow-hidden rounded-2xl ${
              i === 1 ? "md:row-span-2" : ""
            }`}
          >
            <img
              src={src}
              alt="Southern Greens completed lawn"
              className="w-full h-[260px] md:h-[300px] object-cover hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SpecialOfferSection = () => (
  <section
    className="py-20 md:py-28 bg-[#F5F5F0] reveal"
    data-testid="special-offer-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
          Limited Time
        </span>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
          Special Lawn Care Offer
        </h2>
        <p className="mt-4 text-[#4B5563] text-lg">
          Mowing, edging, trimming, cleanups, and more — licensed, insured, and
          locally owned. Lock in your spot today.
        </p>
      </div>

      <div className="mt-12 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <div className="relative rounded-3xl overflow-hidden border border-[#D4D4CE] shadow-xl bg-white">
            <img
              src={ASSETS.flier}
              alt="Southern Greens Lawn Care special offer flier — call 479-684-8439 for mowing, edging, trimming and cleanups."
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-4">
          <div className="bg-[#0E3A1F] text-white rounded-3xl p-8 md:p-10">
            <div className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
              Ready to book?
            </div>
            <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold leading-tight">
              Claim your free quote today.
            </h3>
            <p className="mt-4 text-white/85">
              Tell us about your yard — we’ll get back to you with honest
              pricing, usually the same day.
            </p>
            <Link
              to="/quote"
              data-testid="special-offer-quote-button"
              className="mt-7 inline-flex items-center justify-center gap-2 w-full sg-btn-primary rounded-full px-6 py-4 font-bold text-base md:text-lg"
            >
              Request a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={COMPANY.telHref}
              data-testid="special-offer-call-link"
              className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-white text-[#0E3A1F] rounded-full px-6 py-4 font-bold text-base md:text-lg hover:-translate-y-0.5 transition-all"
            >
              <Phone className="h-5 w-5" /> {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section
    className="relative py-20 md:py-28 overflow-hidden reveal"
    data-testid="cta-section"
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${ASSETS.photos.backyard})` }}
    />
    <div className="absolute inset-0 bg-[#0E3A1F]/85" />
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
      <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">
        Ready for a yard you don’t have to think about?
      </h2>
      <p className="mt-5 text-white/85 text-lg max-w-2xl mx-auto">
        Tell us about your property and we’ll get back to you with a fair,
        no-pressure quote — usually the same day.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          href={COMPANY.telHref}
          data-testid="cta-call-link"
          className="inline-flex items-center gap-3 bg-white text-[#0E3A1F] rounded-full px-7 py-4 font-bold text-lg shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <Phone className="h-5 w-5" /> Call {COMPANY.phoneDisplay}
        </a>
        <Link
          to="/quote"
          data-testid="cta-quote-button"
          className="sg-btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 font-bold text-lg"
        >
          Request a Free Quote <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustBar />
      <IntroSection />
      <ServicesPreview />
      <GalleryStrip />
      <SpecialOfferSection />
      <CTASection />
    </div>
  );
};

export default Home;
